import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Inline, Stack } from '@atlaskit/primitives/compiled';
import {
	ConnectAppButton,
	type ConnectAppProduct,
} from '@atlassian/rovo-settings-modal/ui/connect-app-button';

const INSIGHTS_CONNECT_APP_PRODUCT_IDS: readonly ConnectAppProduct[] = ['slack', 'google-calendar'];

export const InsightsConnectAppsAction = ({
	isCompact,
	onVisibilityChange,
}: {
	isCompact: boolean;
	onVisibilityChange: (isVisible: boolean) => void;
}): React.JSX.Element => {
	const [visibleProducts, setVisibleProducts] = useState<ReadonlySet<ConnectAppProduct>>(
		() => new Set(),
	);

	const handleProductVisibilityChange = useCallback(
		(product: ConnectAppProduct, isVisible: boolean) => {
			setVisibleProducts((currentProducts) => {
				if (currentProducts.has(product) === isVisible) {
					return currentProducts;
				}

				const nextProducts = new Set(currentProducts);
				if (isVisible) {
					nextProducts.add(product);
				} else {
					nextProducts.delete(product);
				}

				return nextProducts;
			});
		},
		[],
	);

	useEffect(() => {
		onVisibilityChange(visibleProducts.size > 0);
	}, [onVisibilityChange, visibleProducts]);

	// Stable per-product callbacks to keep ConnectAppButton's visibility effect from re-firing.
	const productVisibilityHandlers = useMemo(
		() =>
			new Map(
				INSIGHTS_CONNECT_APP_PRODUCT_IDS.map((product) => [
					product,
					(isVisible: boolean) => handleProductVisibilityChange(product, isVisible),
				]),
			),
		[handleProductVisibilityChange],
	);

	const buttons = INSIGHTS_CONNECT_APP_PRODUCT_IDS.map((product) => (
		<ConnectAppButton
			key={product}
			product={product}
			isCompact={isCompact}
			analyticsSource="insightsFeed"
			analyticsSurface="chat"
			onVisibilityChange={productVisibilityHandlers.get(product)}
		/>
	));

	return isCompact ? (
		<Inline space="space.100" shouldWrap>
			{buttons}
		</Inline>
	) : (
		<Stack space="space.050" alignInline="end">
			{buttons}
		</Stack>
	);
};
